import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const OUTPUT_SIZE = 400;

// "cover" fit: menor lado da imagem preenche exatamente o viewport quando scale=1.
const getEffectiveScale = (img, viewport, userScale) => {
    if (!img) return 1;
    const base = viewport / Math.min(img.naturalWidth, img.naturalHeight);
    return base * userScale;
};

const clampOffset = (img, viewport, effScale, offset) => {
    if (!img) return { x: 0, y: 0 };
    const renderedW = img.naturalWidth * effScale;
    const renderedH = img.naturalHeight * effScale;
    const maxX = Math.max(0, (renderedW - viewport) / 2);
    const maxY = Math.max(0, (renderedH - viewport) / 2);
    return {
        x: Math.min(maxX, Math.max(-maxX, offset.x)),
        y: Math.min(maxY, Math.max(-maxY, offset.y)),
    };
};

// Tamanho do círculo de recorte: mínimo 250px (mobile), cresce até 400px
// deixando folga pro header, controles de zoom e a barra de botões.
const computeViewport = () => {
    if (typeof window === "undefined") return 280;
    const avail = Math.min(window.innerWidth - 48, window.innerHeight - 300);
    return Math.round(Math.max(250, Math.min(400, avail)));
};

/**
 * ImageCropperModal — recorte de foto de perfil no estilo Instagram/WhatsApp:
 * arrasta pra reposicionar, pinça ou rola o mouse pra dar zoom, guia circular
 * de enquadramento. Exporta um JPEG quadrado 400x400 em base64 — o círculo é
 * só o guia visual de edição (JPEG não tem alpha); a máscara redonda/
 * arredondada na exibição fica a cargo de quem renderiza o avatar depois.
 *
 * Renderizado via portal no <body>: a seção "Dados Cadastrais" das
 * Configurações usa `backdrop-blur-xl` (backdrop-filter), e um ancestral com
 * backdrop-filter/filter/transform vira o *containing block* de descendentes
 * `position: fixed` no Chromium/WebKit. Sem o portal, o `fixed inset-0` do
 * overlay era resolvido em relação ao Card (não à viewport) — o overlay não
 * cobria a tela toda (a seção "Endereço" dourada vazava por trás) e o modal
 * `h-[100dvh]` empurrava os botões pra fora da área visível. O portal tira o
 * nó desse ancestral e devolve o comportamento viewport-relative.
 *
 * Layout em coluna: header no topo, canvas de recorte ocupando o espaço do
 * meio, botões Cancelar/Confirmar sempre fixos no rodapé (nunca escondidos
 * pelo canvas, nem no mobile de 375px). Scroll do body/html travado enquanto
 * aberto.
 *
 * Só canvas + eventos de mouse/touch nativos — sem libs externas.
 */
const ImageCropperModal = ({ file, onConfirm, onCancel, onChangePhoto }) => {
    const [img, setImg] = useState(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [viewport, setViewport] = useState(computeViewport);

    const viewportRef = useRef(null);
    const scaleRef = useRef(1);
    const offsetRef = useRef({ x: 0, y: 0 });
    const dragRef = useRef(null);

    useEffect(() => { scaleRef.current = scale; }, [scale]);
    useEffect(() => { offsetRef.current = offset; }, [offset]);

    // Trava o scroll da página atrás do modal enquanto ele está aberto.
    // body E html — dependendo do layout (janela vs. kiosk) o scroller muda.
    useEffect(() => {
        const body = document.body;
        const html = document.documentElement;
        const prevBody = body.style.overflow;
        const prevHtml = html.style.overflow;
        body.style.overflow = "hidden";
        html.style.overflow = "hidden";
        return () => {
            body.style.overflow = prevBody;
            html.style.overflow = prevHtml;
        };
    }, []);

    // Recalcula o tamanho do círculo em resize / rotação de tela.
    useEffect(() => {
        const onResize = () => setViewport(computeViewport());
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
        };
    }, []);

    // Se o viewport mudou (resize), re-clampa o offset pra imagem não "vazar".
    useEffect(() => {
        setOffset((o) => clampOffset(img, viewport, getEffectiveScale(img, viewport, scaleRef.current), o));
    }, [viewport, img]);

    // Carrega a imagem selecionada (object URL — mais leve que base64 só pra exibir)
    useEffect(() => {
        if (!file) { setImg(null); return; }
        const url = URL.createObjectURL(file);
        const el = new Image();
        el.onload = () => setImg(el);
        el.src = url;
        setScale(1);
        setOffset({ x: 0, y: 0 });
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const applyZoom = (nextScale) => {
        const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
        setScale(clamped);
        setOffset((o) => clampOffset(img, viewport, getEffectiveScale(img, viewport, clamped), o));
    };

    // Drag do mouse (desktop) — listeners na window pra não perder o arrasto
    // se o cursor sair do círculo.
    const onMouseDown = (e) => {
        dragRef.current = { x: e.clientX, y: e.clientY };
    };
    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current) return;
            const dx = e.clientX - dragRef.current.x;
            const dy = e.clientY - dragRef.current.y;
            dragRef.current = { x: e.clientX, y: e.clientY };
            const eff = getEffectiveScale(img, viewport, scaleRef.current);
            setOffset((o) => clampOffset(img, viewport, eff, { x: o.x + dx, y: o.y + dy }));
        };
        const onUp = () => { dragRef.current = null; };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [img, viewport]);

    // Touch (pan 1 dedo / pinch 2 dedos) + scroll do mouse — listeners nativos
    // non-passive pra poder preventDefault (trava o scroll da página atrás do
    // modal enquanto o usuário mexe na foto).
    useEffect(() => {
        const el = viewportRef.current;
        if (!el || !img) return;
        const state = { mode: null, lastX: 0, lastY: 0, startDist: 0, startScale: 1 };
        const dist = (t0, t1) => Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);

        const handleWheel = (e) => {
            e.preventDefault();
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scaleRef.current - e.deltaY * 0.0015));
            setScale(next);
            setOffset((o) => clampOffset(img, viewport, getEffectiveScale(img, viewport, next), o));
        };
        const handleTouchStart = (e) => {
            if (e.touches.length === 1) {
                state.mode = "pan";
                state.lastX = e.touches[0].clientX;
                state.lastY = e.touches[0].clientY;
            } else if (e.touches.length === 2) {
                state.mode = "pinch";
                state.startDist = dist(e.touches[0], e.touches[1]) || 1;
                state.startScale = scaleRef.current;
            }
        };
        const handleTouchMove = (e) => {
            e.preventDefault();
            if (state.mode === "pan" && e.touches.length === 1) {
                const t = e.touches[0];
                const dx = t.clientX - state.lastX;
                const dy = t.clientY - state.lastY;
                state.lastX = t.clientX;
                state.lastY = t.clientY;
                const eff = getEffectiveScale(img, viewport, scaleRef.current);
                setOffset((o) => clampOffset(img, viewport, eff, { x: o.x + dx, y: o.y + dy }));
            } else if (state.mode === "pinch" && e.touches.length === 2) {
                const d = dist(e.touches[0], e.touches[1]);
                const ratio = d / state.startDist;
                const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, state.startScale * ratio));
                setScale(next);
                setOffset((o) => clampOffset(img, viewport, getEffectiveScale(img, viewport, next), o));
            }
        };
        const handleTouchEnd = (e) => {
            if (e.touches.length === 0) {
                state.mode = null;
            } else if (e.touches.length === 1) {
                state.mode = "pan";
                state.lastX = e.touches[0].clientX;
                state.lastY = e.touches[0].clientY;
            }
        };

        el.addEventListener("wheel", handleWheel, { passive: false });
        el.addEventListener("touchstart", handleTouchStart, { passive: true });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        el.addEventListener("touchend", handleTouchEnd, { passive: true });
        el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
        return () => {
            el.removeEventListener("wheel", handleWheel);
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [img, viewport]);

    const handleConfirm = () => {
        if (!img) return;
        const eff = getEffectiveScale(img, viewport, scale);
        const k = OUTPUT_SIZE / viewport;
        const renderedW = img.naturalWidth * eff;
        const renderedH = img.naturalHeight * eff;
        const dx = (viewport / 2 - renderedW / 2 + offset.x) * k;
        const dy = (viewport / 2 - renderedH / 2 + offset.y) * k;

        const canvas = document.createElement("canvas");
        canvas.width = OUTPUT_SIZE;
        canvas.height = OUTPUT_SIZE;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        ctx.drawImage(img, dx, dy, renderedW * k, renderedH * k);
        onConfirm(canvas.toDataURL("image/jpeg", 0.92));
    };

    const eff = getEffectiveScale(img, viewport, scale);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            data-testid="image-cropper-modal"
            className="fixed inset-0 z-[2147483647] overflow-hidden bg-black flex sm:items-center sm:justify-center sm:bg-black/85 sm:backdrop-blur-sm"
            style={{ position: "fixed", inset: 0 }}
            onClick={(e) => e.target === e.currentTarget && onCancel()}
        >
            {/* Modal: fullscreen no mobile, card centrado (max 500px) no desktop.
                Coluna: header (topo) · canvas (meio, flex-1) · botões (rodapé, fixos). */}
            <div className="flex flex-col w-full h-[100dvh] bg-slate-950 overflow-hidden sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-[500px] sm:rounded-2xl sm:border sm:border-amber-500/30 sm:shadow-[0_0_80px_-20px_rgba(212,175,55,0.5)]">
                {/* Header */}
                <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <h3 className="salon-heading text-lg text-amber-200">Ajustar foto</h3>
                    <button
                        type="button"
                        data-testid="cropper-close"
                        onClick={onCancel}
                        className="h-9 w-9 rounded-lg border border-white/10 hover:border-white/30 text-slate-400 hover:text-white transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                {/* Área do meio — ocupa todo o espaço entre header e rodapé.
                    Rola internamente se a tela for muito baixa; os botões nunca somem. */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col items-center justify-center gap-4 px-4 py-4">
                    <p className="text-[11px] text-slate-500 text-center">
                        Arraste pra reposicionar · pinça ou role o mouse pra dar zoom
                    </p>

                    <div
                        ref={viewportRef}
                        data-testid="cropper-viewport"
                        onMouseDown={onMouseDown}
                        style={{ width: viewport, height: viewport, touchAction: "none" }}
                        className="relative shrink-0 rounded-full overflow-hidden border-2 border-amber-400/70 bg-black cursor-grab active:cursor-grabbing select-none shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]"
                    >
                        {img && (
                            <img
                                src={img.src}
                                alt=""
                                draggable={false}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    width: img.naturalWidth * eff,
                                    height: img.naturalHeight * eff,
                                    maxWidth: "none",
                                    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                                }}
                            />
                        )}
                    </div>

                    <div className="flex items-center justify-center gap-2 text-slate-500 w-full max-w-[360px]">
                        <i className="fa-solid fa-magnifying-glass-minus text-xs"></i>
                        <input
                            type="range"
                            min="1"
                            max="4"
                            step="0.01"
                            value={scale}
                            onChange={(e) => applyZoom(parseFloat(e.target.value))}
                            className="flex-1 accent-amber-500"
                            data-testid="cropper-zoom-slider"
                        />
                        <i className="fa-solid fa-magnifying-glass-plus text-xs"></i>
                    </div>

                    <button
                        type="button"
                        data-testid="cropper-change-photo"
                        onClick={onChangePhoto}
                        className="w-full max-w-[360px] min-h-[44px] rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-xs uppercase tracking-wider font-bold transition-all"
                    >
                        <i className="fa-solid fa-image mr-2"></i>Trocar foto
                    </button>
                </div>

                {/* Rodapé — botões sempre visíveis, respeitando a safe-area do iPhone. */}
                <footer
                    className="shrink-0 flex items-center gap-2 px-4 pt-3 border-t border-white/5"
                    style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
                >
                    <button
                        type="button"
                        data-testid="cropper-cancel"
                        onClick={onCancel}
                        className="flex-1 min-h-[44px] rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-xs uppercase tracking-wider font-bold transition-all"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        data-testid="cropper-confirm"
                        onClick={handleConfirm}
                        disabled={!img}
                        className="flex-1 min-h-[44px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs uppercase tracking-wider font-bold transition-all disabled:opacity-40"
                    >
                        Confirmar
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    );
};

export default ImageCropperModal;
